// biome-ignore-all lint/suspicious/noArrayIndexKey: index keys are safe here; lists are derived from render state in a stable order and items are not reordered or removed mid-render (Task #32).
// =============================================================================
// ChildItemReadView.jsx — read-only detailed panel for one child + category
// =============================================================================
// Renders the SAME detailed item panels the responder sees (fees / uniforms /
// shoes / device), but strictly READ-ONLY: no Edit button, no status dropdown,
// no Locked label, no per-item Print button. A read-only status badge shows the
// outcome (Pending / Approved / Declined / Needs Info). Uniforms include the
// REIMB tag and the School Locker reference (OSD code) when present.
//
// Used by the shelter view (ShelterRequestView) so shelters and responders see
// matching item detail. This is Phase 1 of the shelter/responder parity work:
// the responder (ResponderChildView) still renders its own inline read blocks
// for now — Phase 2 will migrate it onto this shared component so there is a
// single source of truth. Until then the markup here is intentionally kept in
// step with the responder's read blocks.
//
// Props:
//   child - one child object (fees[], uniforms{}, shoes{}, laptop{}, plus
//           gender / yearLevel / schoolName)
//   cat   - "fees" | "uniforms" | "shoes" | "laptop"
// =============================================================================
import { Fragment } from "react";
import { useByodShowResponders, useShowDeviceDelivery } from "../../app/UiConfigContext.jsx";
import { getFileSignedUrl } from "../../data/storage";
import { PANEL } from "../../utils/constants";
import { APPROVAL_STATUSES } from "../../utils/enums";
import { feeTypeLabel, isPrimary } from "../../utils/formConstants";
import { GLYPH } from "../../utils/glyphs";
import { money } from "../../utils/helpers";
import { formatPhone } from "../../utils/sanitize";
import { HelpButton } from "../form/HelpButton.jsx";
import { UniformTable } from "../form/UniformTable.jsx";
import { ByodLookupPanel } from "./ByodLookupPanel.jsx";
import { RepeatFlagPanel } from "./RepeatFlagPanel.jsx";
import { APPROVAL_COLOURS, ROField } from "./RequestDetailAtoms.jsx";

// Per-fee-type help keys — the "?" bubbles on each School Costs & Stationery row.
// Each fee type maps to a help_content key; the text is edited in Admin -> Help
// Content (keys registered in HelpContentAdmin) and seeded by docs/db-33.
const SCC_HELP_KEYS = {
  Stationery: "scc_stationery",
  Textbooks: "scc_textbooks",
  "Stationery & Textbooks": "scc_stationery_textbooks",
  "Camp Fees": "scc_camp_fees",
  "Resource Fees": "scc_resource_fees",
  "Device Hire": "scc_device_hire",
  "School Activities": "scc_school_activities",
  "Non-school Activities": "scc_non_school_activities",
  "Swim Gear": "scc_swim_gear",
  "Other (specify)": "scc_other",
};

// Read-only status pill — mirrors the responder's status <select>, minus editing.
function StatusBadge({ status }) {
  const st = status || APPROVAL_STATUSES.PENDING;
  const c = APPROVAL_COLOURS[st] || APPROVAL_COLOURS.Pending;
  return (
    <span
      style={{
        padding: "6px 14px",
        borderRadius: 8,
        border: `2px solid ${c.border}`,
        fontSize: 13,
        fontWeight: 700,
        background: c.bg,
        color: c.text,
        whiteSpace: "nowrap",
      }}
    >
      {st}
    </span>
  );
}

// Shared heading style for the coloured category boxes.
function boxTitleStyle(color) {
  return {
    fontSize: 12,
    fontWeight: 700,
    color: color,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };
}

// showBadge controls whether the read-only category status pill renders inside
// the panel header (uniforms/shoes/device). The shelter shows it (default); the
// responder passes false because it shows the editable status dropdown in its
// own outer header.
// renderFeeStatus(fee, fi) is an optional slot for the per-fee status control.
// Default: a read-only badge (shelter). The responder passes a slot returning its
// editable status <select>.
// uniformsActions is an optional node rendered in the uniforms panel header (left
// of the OSD ref / badge). The responder passes its Print button + live School
// Locker status here; the shelter passes nothing.
export function ChildItemReadView({
  child,
  cat,
  showBadge = true,
  renderFeeStatus,
  uniformsActions,
  feeHelpUser,
  // Internal (Responder) view only: show the SO Reference / Order Number fields.
  // DFV Service (shelter) users never see them (default false).
  internal = false,
  // Possible-repeat flag (db-210), INTERNAL users only: the ItemRepeat map keyed by fee /
  // device id (src/services/repeatCheck.ts), the latest Repeat check note, and the
  // look-back months. Ignored unless `internal` — a DFV Service never sees the flag.
  repeats = null,
  repeatReview = null,
  repeatMonths = 12,
}) {
  // Admin-controlled per-device delivery visibility (default off). System Setup → Config.
  const showDeviceDelivery = useShowDeviceDelivery();
  // BYOD Stage 3: the "BYOD AI" panel shows only to responders (internal), only when
  // the switch is on, and only for a child whose school was picked from the list.
  const byodShowResponders = useByodShowResponders();
  if (!child) return null;
  const repeatFor = function (id) {
    return internal && repeats && id ? repeats[id] || null : null;
  };

  if (cat === "fees") {
    if (!child.fees || child.fees.length === 0) return null;
    return (
      <div
        style={{
          padding: "12px 14px",
          background: PANEL.bg,
          borderRadius: 8,
          borderLeft: `4px solid ${PANEL.border}`,
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: PANEL.heading,
            textTransform: "uppercase",
            letterSpacing: 0.4,
            marginBottom: 10,
          }}
        >
          School Costs &amp; Stationery
        </div>
        {child.fees.map(function (f, fi) {
          const fSt = f.approvalStatus || APPROVAL_STATUSES.PENDING;
          const fCol = APPROVAL_COLOURS[fSt] || APPROVAL_COLOURS.Pending;
          const feeKey = SCC_HELP_KEYS[f.feeType];
          const rowHelp = !!feeHelpUser && !!feeKey;
          // Pack the fields tightly left (Amount right after the help "?", not
          // stretched), cap the Description (John's form rule: size fields to the
          // data), and — for internal users — always reserve the SO No. + Order
          // No. columns so the row layout is stable and there's room for the
          // Order No. even before a picker fills it. A trailing 1fr absorbs the
          // remaining width on the right instead of gapping between fields.
          const feeCols =
            (rowHelp ? "180px auto 95px " : "180px 95px ") +
            "minmax(180px, 300px)" +
            (internal ? " 140px 140px" : "") +
            " 1fr";
          const brochureLabel =
            f.feeType === "Camp Fees"
              ? "Camp Brochure"
              : f.feeType === "Device Hire"
                ? "Device Hire Agmt"
                : "School Letter";
          const feeRepeat = repeatFor(f.id);
          return (
            <Fragment key={fi}>
              <div
                data-repeat-flagged={feeRepeat ? "true" : undefined}
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: 8,
                  marginBottom: feeRepeat ? 0 : 8,
                  background: fCol.bg,
                  border: feeRepeat ? "2px solid #FFB300" : `1px solid ${fCol.border}`,
                  borderRadius: feeRepeat ? "6px 6px 0 0" : 6,
                  padding: "8px 10px",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    display: "grid",
                    gridTemplateColumns: feeCols,
                    gap: 8,
                  }}
                >
                  {/* Fee Type wraps inside its box (no nowrap) — a long label like
                    "Generic (Grade based) Stationery" flows to a second line within
                    the fixed 180px column instead of overflowing (John, 2 Aug 2026). */}
                  <ROField label="Fee Type" value={feeTypeLabel(f.feeType)} />
                  {rowHelp && (
                    <div>
                      <div
                        className="text-[11px] font-bold uppercase tracking-[0.4px] mb-1"
                        style={{ visibility: "hidden" }}
                      >
                        ?
                      </div>
                      <div className="min-h-9 flex items-center">
                        <HelpButton helpKey={feeKey} user={feeHelpUser} />
                      </div>
                    </div>
                  )}
                  <ROField
                    // The QLD Stationery fee is a picker item — its amount is the actual
                    // charged cost, so label it "Actual Cost" (other fee types stay "Amount").
                    label={f.feeType === "Stationery" ? "Actual Cost" : "Amount"}
                    value={`$${Number(f.feeAmount || 0).toLocaleString("en-AU", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`}
                    nowrap
                  />
                  <ROField label="Description" value={f.feeDesc} />
                  {/* SO No. / Order No. — PICKER-item fields, so ONLY the QLD Stationery fee
                    shows them (other fee types — camp/resource/etc. — aren't picker-ordered).
                    Both shown once the fee is Approved, even when empty ("—" = not ordered yet).
                    SO No. = Responders only; Order No. = shelters AND responders. */}
                  {internal &&
                    f.feeType === "Stationery" &&
                    f.approvalStatus === APPROVAL_STATUSES.APPROVED && (
                      <ROField label="SO No." value={f.feesSoNumber || "—"} nowrap />
                    )}
                  {f.feeType === "Stationery" &&
                    f.approvalStatus === APPROVAL_STATUSES.APPROVED && (
                      <ROField label="Order No." value={f.feesOrderNumber || "—"} nowrap />
                    )}
                </div>
                {f.brochure && (
                  <button
                    type="button"
                    onClick={async function () {
                      // Private bucket (db-168): sign the stored PATH on demand.
                      const u = await getFileSignedUrl(f.brochure);
                      if (u) window.open(u, "_blank", "noopener,noreferrer");
                    }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      background: "#E0F2F1",
                      border: "1px solid #00897B40",
                      borderRadius: 7,
                      // Trailing padding halved (John, 23 Aug 2026) — the label already
                      // ends in a download arrow, so the right gap read as too wide.
                      padding: "7px 6px 7px 12px",
                      fontSize: 13,
                      color: "#00695C",
                      cursor: "pointer",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {`\u{1F4C4} ${brochureLabel} ${GLYPH.arrowDown}`}
                  </button>
                )}
                {renderFeeStatus ? renderFeeStatus(f, fi) : <StatusBadge status={fSt} />}
              </div>
              {feeRepeat && (
                <RepeatFlagPanel repeat={feeRepeat} review={repeatReview} months={repeatMonths} />
              )}
            </Fragment>
          );
        })}
      </div>
    );
  }

  if (cat === "uniforms") {
    if (!child.uniforms) return null;
    const u = child.uniforms;
    return (
      <div
        style={{
          padding: "12px 14px",
          background: PANEL.bg,
          borderRadius: 8,
          borderLeft: `4px solid ${PANEL.border}`,
        }}
      >
        <div style={boxTitleStyle(PANEL.heading)}>
          <span>
            {`Uniforms — ${u.supplier || "—"}`}
            {u.isReimbursement && (
              <span
                style={{
                  marginLeft: 8,
                  fontSize: 10,
                  background: "#FFF3E0",
                  color: "#E65100",
                  border: "1px solid #FFB300",
                  borderRadius: 4,
                  padding: "2px 6px",
                }}
              >
                REIMB
              </span>
            )}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {uniformsActions}
            {u.osdCode && (
              <span
                style={{
                  fontSize: 12,
                  color: PANEL.heading,
                  fontWeight: 700,
                  background: PANEL.bg,
                  border: "1.5px solid #1565C0",
                  borderRadius: 6,
                  padding: "4px 12px",
                  whiteSpace: "nowrap",
                }}
                title={
                  u.osdReceivedAt
                    ? `Received ${new Date(u.osdReceivedAt).toLocaleString("en-AU")}`
                    : ""
                }
              >
                {`School Locker Ref: ${u.osdCode}`}
              </span>
            )}
            {showBadge && <StatusBadge status={u.approvalStatus} />}
          </div>
        </div>
        {u.authorisedPerson && <ROField label="Authorised Person" value={u.authorisedPerson} />}
        {u.authorisedContact && <ROField label="Authorised Contact" value={u.authorisedContact} />}
        {u.supplier === "School Locker" && (u.mobile || u.email) && (
          <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
            {u.mobile && (
              <ROField label="Mobile (SMS pickup)" value={formatPhone(u.mobile, "mobile")} />
            )}
            {u.email && <ROField label="Email (notification)" value={u.email} />}
          </div>
        )}
        <UniformTable
          child={Object.assign({}, u, {
            gender: child.gender,
            yearLevel: child.yearLevel,
            schoolName: child.schoolName,
          })}
          onChange={function () {}}
          showSku={u.supplier === "School Locker"}
          isCold={u.isColdClimate}
          locked={true}
        />
      </div>
    );
  }

  if (cat === "shoes") {
    if (!child.shoes) return null;
    const s = child.shoes;
    return (
      <div
        style={{
          padding: "12px 14px",
          background: PANEL.bg,
          borderRadius: 8,
          borderLeft: `4px solid ${PANEL.border}`,
        }}
      >
        <div style={boxTitleStyle(PANEL.heading)}>
          <span>Shoes</span>
          {showBadge && <StatusBadge status={s.approvalStatus} />}
        </div>
        {/* Two halves: the Joggers pair sits on the left, the Leather pair starts
            mid-panel (col 2 = 50%). Within each half the Qty + Cost boxes sit
            tight together (a flex row), not spread across the whole panel. */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <ROField
              label={
                <span>
                  <span style={{ fontSize: "1.2em" }}>Joggers</span>
                  <br />
                  Qty
                </span>
              }
              value={String(s.jQty || 0)}
              boxStyle={{ maxWidth: 70 }}
            />
            <ROField
              label="Cost"
              value={s.jCost ? money(s.jCost) : "—"}
              boxStyle={{ maxWidth: 95 }}
            />
          </div>
          {!isPrimary(child.yearLevel) && (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <ROField
                label={
                  <span>
                    <span style={{ fontSize: "1.2em" }}>Leather</span>
                    <br />
                    Qty
                  </span>
                }
                value={String(s.lQty || 0)}
                boxStyle={{ maxWidth: 70 }}
              />
              <ROField
                label="Cost"
                value={s.lCost ? money(s.lCost) : "—"}
                boxStyle={{ maxWidth: 95 }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (cat === "laptop") {
    if (!child.laptop) return null;
    const l = child.laptop;
    // db-168: byod now stores an object PATH (not an http URL), so recognise any
    // non-empty string as an uploaded doc; getFileSignedUrl signs it (path or legacy URL).
    const isByodUrl = l.byod && typeof l.byod === "string";
    return (
      <div
        style={{
          padding: "12px 14px",
          background: PANEL.bg,
          borderRadius: 8,
          borderLeft: `4px solid ${PANEL.border}`,
        }}
      >
        <div style={boxTitleStyle(PANEL.heading)}>
          <span>Device</span>
          {showBadge && <StatusBadge status={l.approvalStatus} />}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 8 }}>
          {/* Top row in its own nested grid: Device Type halved (1fr) and that
              width handed to Est. Cost (3fr) so its long "- No Authority to
              purchase" label fits on one line. The acknowledgement row below stays
              a 5-equal-column layout in the parent grid. */}
          <div
            style={{
              gridColumn: "span 5",
              display: "grid",
              gridTemplateColumns: "1fr 3fr 2fr 2fr 2fr",
              gap: 8,
            }}
          >
            <ROField label="Device Type" value={l.deviceType} />
            <ROField
              // Once a picker records the supplier Order No., the figure is the ACTUAL
              // charged cost, not an estimate — so the label drops "Est."/"No Authority"
              // (shelters + responders see this too). Blank Order No. = not ordered yet.
              label={
                l.deviceOrderNumber && String(l.deviceOrderNumber).trim()
                  ? "Actual Cost - Ordered by Zephyr"
                  : "Est. Cost - No Authority to purchase"
              }
              value={l.estimatedCost ? money(l.estimatedCost) : "—"}
            />
            {/* SO No. — Responders only (internal); always shown once the device is
                Approved, even when empty ("—"). */}
            {internal && l.approvalStatus === APPROVAL_STATUSES.APPROVED && (
              <ROField label="SO No." value={l.deviceSoNumber || "—"} />
            )}
            {/* Product Code — Responders only (internal); the Unleashed stock code, which
                starts as the mapped placeholder (e.g. DEV_LAP) at approval and updates to
                the picker's chosen code. */}
            {internal && l.approvalStatus === APPROVAL_STATUSES.APPROVED && (
              <ROField label="Product Code" value={l.deviceProductCode || "—"} />
            )}
            {/* Order No. — shows on an APPROVED device for shelters AND responders, even
                when empty ("—"): a blank Order No. tells them it hasn't been ordered yet. */}
            {l.approvalStatus === APPROVAL_STATUSES.APPROVED && (
              <ROField label="Order No." value={l.deviceOrderNumber || "—"} />
            )}
          </div>
          <ROField label="Why Needed" value={l.whyNeeded} span={5} />
          {child.yearLevel === "TAFE" && l.tafeCourse && (
            <ROField label="Course Name" value={l.tafeCourse} span={2} />
          )}
          {child.yearLevel === "TAFE" && l.tafeStartDate && (
            <ROField label="Course Start Date" value={l.tafeStartDate} />
          )}
          {child.yearLevel === "TAFE" && l.tafeLength && (
            <ROField label="Length of Course" value={l.tafeLength} />
          )}
          {showDeviceDelivery && l.deliveryAddress && (
            <ROField label="Delivery Address" value={l.deliveryAddress} span={5} />
          )}
          {child.yearLevel !== "TAFE" && (
            <ROField label="BYOD Policy" value={l.hasSchoolDevice ? "Yes" : "No"} />
          )}
          <ROField label="Internet Safety" value={l.internetSafety ? "Yes" : "No"} />
          <ROField label="Terms Accepted" value={l.termsAccepted ? "Yes" : "No"} />
          <ROField label="Estimate Understood" value="Yes" />
          {isByodUrl ? (
            <div style={{ gridColumn: "span 1" }}>
              <div
                style={{
                  fontSize: 11,
                  color: "#607D79",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  marginBottom: 4,
                }}
              >
                {child.yearLevel === "TAFE" ? "Enrolment Doc" : "BYOD Doc"}
              </div>
              <button
                type="button"
                onClick={async function () {
                  // Private bucket (db-168): sign the stored PATH on demand.
                  const u = await getFileSignedUrl(l.byod);
                  if (u) window.open(u, "_blank", "noopener,noreferrer");
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: "#E0F2F1",
                  border: "1px solid #00897B40",
                  borderRadius: 7,
                  padding: "7px 12px",
                  fontSize: 13,
                  color: "#00695C",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                {`\u{1F4C4} ${child.yearLevel === "TAFE" ? "Enrolment Document" : "BYOD Document"} ${GLYPH.arrowDown}`}
              </button>
            </div>
          ) : (
            <ROField
              label={child.yearLevel === "TAFE" ? "Enrolment Doc" : "BYOD Doc"}
              value="Not uploaded"
            />
          )}
        </div>
        {repeatFor(l.id) && (
          <div style={{ marginTop: 10 }}>
            <RepeatFlagPanel repeat={repeatFor(l.id)} review={repeatReview} months={repeatMonths} />
          </div>
        )}
        {internal && byodShowResponders && child.schoolId && (
          <ByodLookupPanel schoolId={child.schoolId} yearLevel={child.yearLevel} />
        )}
      </div>
    );
  }

  return null;
}
